import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { API, getAuthToken, getUser } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, Download, Search, Package, AlertTriangle, Plus, Pencil, Trash2, Check, X, Warehouse as WarehouseIcon, Lock } from "lucide-react";
import { toast } from "sonner";
import { McfLiveInventory } from "@/components/mcf/McfLiveInventory";
import { currentUserIsAdmin } from "@/components/mcf/mcfUtils";

const EDIT_ACCESS_EMAIL = "simranmalik@wellversed.in";

function getWeekRange(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diffToMon = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diffToMon);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    start: monday.toISOString().split("T")[0],
    end: sunday.toISOString().split("T")[0],
  };
}

export default function InventoryAllocation() {
  const user = getUser();
  const canEdit = user?.email === EDIT_ACCESS_EMAIL;

  const currentWeek = getWeekRange();
  const [weekStart, setWeekStart] = useState(currentWeek.start);
  const [weekEnd, setWeekEnd] = useState(currentWeek.end);
  const todayStr = new Date().toISOString().split("T")[0];
  const isPastWeek = weekEnd < todayStr;
  const [items, setItems] = useState([]);
  const [isFrozen, setIsFrozen] = useState(false);
  const [products, setProducts] = useState([]);

  // Warehouse selector
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState("");
  const selectedWarehouse = useMemo(
    () => warehouses.find((w) => w.id === selectedWarehouseId) || null,
    [warehouses, selectedWarehouseId]
  );
  const isWeekly = !selectedWarehouse || selectedWarehouse.warehouse_type === "weekly_allocation";
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const fileInputRef = useRef(null);

  // Add/Edit modal state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addSkuId, setAddSkuId] = useState("");
  const [addSkuName, setAddSkuName] = useState("");
  const [addQty, setAddQty] = useState("");
  const [saving, setSaving] = useState(false);

  // Inline edit state
  const [editingSku, setEditingSku] = useState(null);
  const [editQty, setEditQty] = useState("");

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Selection state
  const [selectedSkus, setSelectedSkus] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  useEffect(() => {
    fetchAllocations();
  }, [weekStart, weekEnd]);

  useEffect(() => {
    if (canEdit || currentUserIsAdmin()) fetchProducts();
  }, [canEdit]);

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchWarehouses = async () => {
    try {
      const res = await axios.get(`${API}/warehouses`);
      // Weekly Allocation (Gurugram) first, then active FBW (MCF) warehouses with the live ledger.
      const active = (res.data || []).filter((w) => w.is_active);
      const list = [
        ...active.filter((w) => w.warehouse_type === "weekly_allocation"),
        ...active.filter((w) => w.warehouse_type === "fbw_inventory").sort((a, b) => a.warehouse_name.localeCompare(b.warehouse_name)),
      ];
      setWarehouses(list);
      setSelectedWarehouseId(list[0]?.id || "");
    } catch {
      // silent — inventory still works with the default weekly view
    }
  };

  const fetchAllocations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/inventory-allocations?week_start=${weekStart}&week_end=${weekEnd}`
      );
      setItems(response.data.items || []);
      setUpdatedAt(response.data.updated_at);
      setIsFrozen(Boolean(response.data.frozen));
      setSelectedSkus(new Set());
    } catch {
      toast.error("Error fetching inventory allocations");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data || []);
    } catch {
      // silent
    }
  };

  const handleWeekStartChange = (val) => {
    if (!val) return;
    // Auto-snap any picked date to its full Monday→Sunday week (matches backend storage key).
    const wk = getWeekRange(new Date(val + "T00:00:00"));
    setWeekStart(wk.start);
    setWeekEnd(wk.end);
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(
        `${API}/inventory-allocations/upload?week_start=${weekStart}&week_end=${weekEnd}`,
        formData,
        { timeout: 120000 }
      );
      const result = response.data;
      setUploadResult(result);
      if (result.failed === 0) {
        toast.success(`Uploaded ${result.success} SKU allocations successfully`);
      } else {
        toast.warning(`Uploaded ${result.success} SKUs. ${result.failed} failed.`);
      }
      fetchAllocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error uploading file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/inventory-allocations/template`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "inventory_allocation_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error downloading template");
    }
  };

  // --- Add/Update single SKU ---
  const openAddModal = () => {
    setAddSkuId("");
    setAddSkuName("");
    setAddQty("");
    setAddModalOpen(true);
  };

  const handleSkuSelect = (skuId) => {
    setAddSkuId(skuId);
    const product = products.find((p) => p.sku_id === skuId);
    setAddSkuName(product ? product.product_name : "");
    // Pre-fill qty if SKU already has allocation
    const existing = items.find((i) => i.sku_id === skuId);
    if (existing) {
      setAddQty(String(existing.allocated_qty));
    } else {
      setAddQty("");
    }
  };

  const handleAddSave = async () => {
    if (!addSkuId) {
      toast.error("Please select a SKU");
      return;
    }
    const qty = parseInt(addQty, 10);
    if (isNaN(qty) || qty < 0) {
      toast.error("Allocated quantity must be a non-negative number");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/inventory-allocations/item`, {
        week_start: weekStart,
        week_end: weekEnd,
        sku_id: addSkuId,
        allocated_qty: qty,
      });
      toast.success(
        items.find((i) => i.sku_id === addSkuId)
          ? "Allocation updated"
          : "Allocation added"
      );
      setAddModalOpen(false);
      fetchAllocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error saving allocation");
    } finally {
      setSaving(false);
    }
  };

  // --- Inline edit ---
  const startInlineEdit = (item) => {
    setEditingSku(item.sku_id);
    setEditQty(String(item.allocated_qty));
  };

  const cancelInlineEdit = () => {
    setEditingSku(null);
    setEditQty("");
  };

  const saveInlineEdit = async (skuId) => {
    const qty = parseInt(editQty, 10);
    if (isNaN(qty) || qty < 0) {
      toast.error("Quantity must be a non-negative number");
      return;
    }
    try {
      await axios.put(`${API}/inventory-allocations/item`, {
        week_start: weekStart,
        week_end: weekEnd,
        sku_id: skuId,
        allocated_qty: qty,
      });
      toast.success("Allocation updated");
      setEditingSku(null);
      fetchAllocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error updating");
    }
  };

  // --- Delete ---
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/inventory-allocations/item`, {
        data: {
          week_start: weekStart,
          week_end: weekEnd,
          sku_id: deleteTarget.sku_id,
        },
      });
      toast.success(`Removed ${deleteTarget.sku_id}`);
      setDeleteTarget(null);
      fetchAllocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error deleting");
    } finally {
      setDeleting(false);
    }
  };

  // --- Selection ---
  const toggleSelectSku = (skuId) => {
    setSelectedSkus((prev) => {
      const next = new Set(prev);
      if (next.has(skuId)) next.delete(skuId);
      else next.add(skuId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const selectable = filteredItems.filter((i) => !i.not_in_allocation);
    if (selectedSkus.size === selectable.length) {
      setSelectedSkus(new Set());
    } else {
      setSelectedSkus(new Set(selectable.map((i) => i.sku_id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSkus.size === 0) return;
    setDeleting(true);
    try {
      await axios.post(`${API}/inventory-allocations/bulk-delete`, {
        week_start: weekStart,
        week_end: weekEnd,
        sku_ids: Array.from(selectedSkus),
      });
      toast.success(`Removed ${selectedSkus.size} allocations`);
      setBulkDeleteOpen(false);
      fetchAllocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error deleting");
    } finally {
      setDeleting(false);
    }
  };

  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(
      (item) =>
        item.sku_id.toLowerCase().includes(q) ||
        item.sku_name.toLowerCase().includes(q)
    );
  }, [items, searchQuery]);

  const totals = useMemo(() => {
    return filteredItems.reduce(
      (acc, item) => ({
        allocated: acc.allocated + item.allocated_qty,
        consumed: acc.consumed + item.used_qty,
        remaining: acc.remaining + item.remaining_qty,
      }),
      { allocated: 0, consumed: 0, remaining: 0 }
    );
  }, [filteredItems]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Historical (ended) Gurugram weeks are ALLOCATION-locked: no add/edit/delete/upload.
  // Consumed Qty still updates dynamically, so the table stays fully viewable/searchable.
  const editable = canEdit && !isFrozen;
  const colSpan = editable ? 7 : 5;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[#0F0F0F]" data-testid="inventory-allocation-title">
            Inventory Allocation
          </h1>
          <p className="text-muted-foreground mt-1">
            {isWeekly
              ? "Weekly SKU allocation tracking with real-time usage"
              : "Live SKU-wise inventory: Delivered (MCF Stock Transfers GRN) − Consumed (uploaded invoices)"}
          </p>
        </div>
        {canEdit && isWeekly && (
          <div className="flex gap-2">
            <input type="file" ref={fileInputRef} onChange={handleBulkUpload} accept=".csv,.xlsx,.xls" className="hidden" />
            {!isFrozen && (
              <>
                <Button onClick={openAddModal} className="bg-accent hover:bg-accent/90 text-white" data-testid="add-allocation-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Allocation
                </Button>
                <Button onClick={() => fileInputRef.current?.click()} variant="outline" disabled={uploading} data-testid="upload-inventory-button">
                  {uploading ? (
                    <><div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />Processing...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" />Upload</>
                  )}
                </Button>
              </>
            )}
            <Button onClick={downloadTemplate} variant="outline" data-testid="download-inventory-template">
              <Download className="h-4 w-4 mr-2" />
              Template
            </Button>
          </div>
        )}
      </div>

      {/* Warehouse Selector */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-end gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <WarehouseIcon className="h-4 w-4 text-accent" />
                Warehouse
              </Label>
              <Select value={selectedWarehouseId} onValueChange={setSelectedWarehouseId}>
                <SelectTrigger className="w-[340px]" data-testid="warehouse-selector">
                  <SelectValue placeholder="Select warehouse" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id} data-testid={`warehouse-option-${w.warehouse_code}`}>
                      {w.warehouse_name} ({w.warehouse_code}) — {w.warehouse_type === "weekly_allocation" ? "Weekly Allocation" : "Live MCF Inventory"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedWarehouse && (
              <div className="text-sm text-muted-foreground pb-2">
                {selectedWarehouse.warehouse_type === "weekly_allocation"
                  ? "Weekly allocation model"
                  : "Live ledger — no weekly allocation"}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Week Selector — Weekly Allocation (Gurugram) only */}
      {isWeekly && (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-end gap-4">
            <div className="space-y-2">
              <Label htmlFor="week-start">Week Start Date</Label>
              <Input id="week-start" type="date" value={weekStart} onChange={(e) => handleWeekStartChange(e.target.value)} data-testid="week-start-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="week-end">Week End Date</Label>
              <Input id="week-end" type="date" value={weekEnd} readOnly disabled title="Auto-set to the Sunday of the selected week (Monday–Sunday)" data-testid="week-end-input" />
            </div>
            <div className="text-sm text-muted-foreground pb-2">
              {formatDate(weekStart)} &mdash; {formatDate(weekEnd)}
              {isWeekly && updatedAt && (
                <span className="ml-3 text-xs">Last updated: {new Date(updatedAt).toLocaleString("en-IN")}</span>
              )}
            </div>
            {isPastWeek && (
              <div className="pb-2" data-testid="frozen-week-badge">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                  <Lock className="h-3 w-3" />
                  Historical Week — {isWeekly ? "Allocation" : "Opening Inventory"} Locked
                </span>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {isWeekly ? "Allocated Qty" : "Opening Inventory Qty"} is locked. Consumption continues to update based on Invoice Date.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      )}

      {!isWeekly ? (
        <McfLiveInventory key={selectedWarehouse.id} warehouse={selectedWarehouse} products={products} />
      ) : (
      <>
      {/* Summary Cards */}
      {items.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-sm text-muted-foreground">Total Allocated</p>
              <p className="text-2xl font-bold font-mono" data-testid="total-allocated">{totals.allocated.toLocaleString("en-IN")}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-sm text-muted-foreground">Total Consumed</p>
              <p className="text-2xl font-bold font-mono text-amber-600" data-testid="total-consumed">{totals.consumed.toLocaleString("en-IN")}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-sm text-muted-foreground">Total Remaining</p>
              <p className={`text-2xl font-bold font-mono ${totals.remaining < 0 ? "text-red-600" : "text-green-600"}`} data-testid="total-remaining">
                {totals.remaining.toLocaleString("en-IN")}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Allocation Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle>Allocations ({filteredItems.length}{searchQuery ? ` of ${items.length}` : ""})</CardTitle>
            {editable && selectedSkus.size > 0 && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setBulkDeleteOpen(true)}
                data-testid="bulk-delete-button"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                Delete Selected ({selectedSkus.size})
              </Button>
            )}
          </div>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search SKU..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="inventory-search" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10">
                <TableRow>
                  {editable && (
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={filteredItems.length > 0 && selectedSkus.size === filteredItems.length}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all"
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                  )}
                  <TableHead className="w-[160px]">SKU ID</TableHead>
                  <TableHead>SKU Name</TableHead>
                  <TableHead className="text-right w-[140px]">Allocated Qty</TableHead>
                  <TableHead className="text-right w-[120px]">Consumed Qty</TableHead>
                  <TableHead className="text-right w-[140px]">Remaining Qty</TableHead>
                  <TableHead className="w-[180px]">Consumed %</TableHead>
                  {editable && <TableHead className="text-right w-[100px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={colSpan} className="text-center py-12">
                      <div className="flex items-center justify-center gap-2 text-muted-foreground">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        Loading...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={colSpan} className="text-center py-12">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Package className="h-8 w-8 opacity-50" />
                        <p>No allocations found for this week.</p>
                        {editable && <p className="text-xs">Click "Add Allocation" or upload a file.</p>}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredItems.map((item) => (
                    <TableRow key={item.sku_id} data-testid={`inventory-row-${item.sku_id}`} className={`${selectedSkus.has(item.sku_id) ? "bg-accent/5" : ""} ${item.not_in_allocation ? "bg-red-50/60" : ""}`}>
                      {editable && (
                        <TableCell>
                          {!item.not_in_allocation && (
                            <Checkbox
                              checked={selectedSkus.has(item.sku_id)}
                              onCheckedChange={() => toggleSelectSku(item.sku_id)}
                              aria-label={`Select ${item.sku_id}`}
                              data-testid={`select-row-${item.sku_id}`}
                            />
                          )}
                        </TableCell>
                      )}
                      <TableCell className="font-mono text-sm font-medium">
                        <div className="flex items-center gap-1.5">
                          {item.sku_id}
                          {item.not_in_allocation && (
                            <span
                              className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700 whitespace-nowrap"
                              title="This SKU was invoiced/dispatched but is not on this week's allocation. Its quantity is counted in Total Consumed."
                              data-testid={`not-in-allocation-badge-${item.sku_id}`}
                            >
                              Not allocated
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{item.sku_name}</TableCell>
                      <TableCell className="text-right font-mono">
                        {editingSku === item.sku_id ? (
                          <div className="flex items-center justify-end gap-1">
                            <Input
                              type="number"
                              min="0"
                              value={editQty}
                              onChange={(e) => setEditQty(e.target.value)}
                              className="w-24 h-8 text-right"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === "Enter") saveInlineEdit(item.sku_id);
                                if (e.key === "Escape") cancelInlineEdit();
                              }}
                              data-testid={`inline-edit-input-${item.sku_id}`}
                            />
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600" onClick={() => saveInlineEdit(item.sku_id)} data-testid={`inline-save-${item.sku_id}`}>
                              <Check className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground" onClick={cancelInlineEdit}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ) : (
                          item.allocated_qty.toLocaleString("en-IN")
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-amber-600">{item.used_qty.toLocaleString("en-IN")}</TableCell>
                      <TableCell className={`text-right font-mono font-semibold ${item.remaining_qty < 0 ? "text-red-600" : item.remaining_qty === 0 ? "text-amber-600" : "text-green-600"}`}>
                        <div className="flex items-center justify-end gap-1">
                          {item.remaining_qty < 0 && <AlertTriangle className="h-3.5 w-3.5" />}
                          {item.remaining_qty.toLocaleString("en-IN")}
                        </div>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const pct = item.allocated_qty > 0 ? (item.used_qty / item.allocated_qty) * 100 : 0;
                          const displayPct = pct.toFixed(1);
                          const isOver = pct > 100;
                          const isWarn = pct >= 70 && pct <= 100;
                          const barColor = isOver ? "#EF4444" : isWarn ? "#1D9E75" : "#32B4AC";
                          const fillWidth = Math.min(100, pct);
                          return (
                            <div className="flex items-center gap-2" data-testid={`consumed-pct-${item.sku_id}`}>
                              <div className="relative flex-1 h-1.5 rounded-full overflow-hidden bg-[#F1F5F9] min-w-[80px]">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{ width: `${fillWidth}%`, background: barColor }}
                                />
                                {isOver && (
                                  <div
                                    className="absolute top-0 right-0 h-full"
                                    style={{ width: "4px", background: "#EF4444", boxShadow: "0 0 0 1px #FFF inset" }}
                                  />
                                )}
                              </div>
                              <span
                                className="text-xs font-mono font-medium w-12 text-right"
                                style={{ color: isOver ? "#EF4444" : "#1A1A1A" }}
                              >
                                {displayPct}%
                              </span>
                            </div>
                          );
                        })()}
                      </TableCell>
                      {editable && (
                        <TableCell className="text-right">
                          {item.not_in_allocation ? (
                            <span className="text-xs text-muted-foreground italic pr-1">consumed-only</span>
                          ) : (
                          <div className="flex justify-end gap-1">
                            {editingSku !== item.sku_id && (
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => startInlineEdit(item)} title="Edit quantity" data-testid={`edit-allocation-${item.sku_id}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => setDeleteTarget(item)} title="Delete" data-testid={`delete-allocation-${item.sku_id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {!canEdit && (
        <p className="text-xs text-muted-foreground text-center">
          View-only access. Contact {EDIT_ACCESS_EMAIL} for allocation changes.
        </p>
      )}

      {/* Add Allocation Modal */}
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="add-allocation-dialog-title">
              {items.find((i) => i.sku_id === addSkuId) ? "Update" : "Add"} Allocation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>SKU *</Label>
              <SearchableSelect
                options={products.map((p) => ({
                  value: p.sku_id,
                  label: `${p.sku_id} - ${p.product_name}`,
                  subtitle: p.brand,
                  searchTerms: [p.sku_id, p.product_name, p.brand, p.item_code],
                }))}
                value={addSkuId}
                onValueChange={handleSkuSelect}
                placeholder="Search SKU..."
                searchPlaceholder="Type SKU ID or name..."
                data-testid="add-allocation-sku-select"
              />
            </div>
            {addSkuName && (
              <div className="p-2 bg-slate-50 rounded text-sm">
                <span className="text-muted-foreground">Product:</span>{" "}
                <span className="font-medium">{addSkuName}</span>
                {items.find((i) => i.sku_id === addSkuId) && (
                  <span className="ml-2 text-xs text-amber-600">(existing — will update)</span>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="add-qty">Allocated Quantity *</Label>
              <Input
                id="add-qty"
                type="number"
                min="0"
                value={addQty}
                onChange={(e) => setAddQty(e.target.value)}
                placeholder="Enter quantity"
                data-testid="add-allocation-qty-input"
              />
            </div>
            <div className="text-xs text-muted-foreground">
              Week: {formatDate(weekStart)} — {formatDate(weekEnd)}
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleAddSave} disabled={saving} className="flex-1 bg-accent hover:bg-accent/90 text-white" data-testid="save-allocation-button">
                {saving ? "Saving..." : "Save"}
              </Button>
              <Button onClick={() => setAddModalOpen(false)} variant="outline" className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Allocation?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove <span className="font-mono font-semibold">{deleteTarget?.sku_id}</span> ({deleteTarget?.sku_name}) from this week&#39;s allocation?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-destructive hover:bg-destructive/90">
              {deleting ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedSkus.size} Allocations?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {selectedSkus.size} SKU allocation{selectedSkus.size > 1 ? "s" : ""} from this week. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} disabled={deleting} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-bulk-delete">
              {deleting ? "Deleting..." : `Delete ${selectedSkus.size} Items`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upload Result Dialog */}
      <AlertDialog open={!!uploadResult} onOpenChange={(open) => { if (!open) setUploadResult(null); }}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="upload-result-title">Upload Complete</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <div className="flex gap-4">
                  <div className="flex-1 p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-700" data-testid="inv-upload-success">{uploadResult?.success || 0}</p>
                    <p className="text-xs text-green-600">Successful</p>
                  </div>
                  <div className="flex-1 p-3 bg-red-50 border border-red-200 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-700" data-testid="inv-upload-failed">{uploadResult?.failed || 0}</p>
                    <p className="text-xs text-red-600">Failed</p>
                  </div>
                </div>
                {uploadResult?.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-slate-700 mb-1">Error Details:</p>
                    <div className="max-h-48 overflow-y-auto border rounded p-2 bg-slate-50 text-sm">
                      {uploadResult.errors.map((err, i) => (
                        <div key={i} className="py-1 border-b last:border-0 text-red-700">
                          <span className="font-mono text-xs">Row {err.row}:</span> {err.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setUploadResult(null)}>Close</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </>
      )}
    </div>
  );
}
